import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Megaphone, SignalHigh, SignalMedium, SignalLow } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { Segmented } from "@/components/layout/segmented";
import { ChipReferencial, StatusChip } from "@/components/mercado/status-chip";
import { ProdutorShell } from "@/components/mercado/produtor-shell";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { fmtDiaCurto, fmtMoedaBR } from "@/lib/formato-mercado";
import { diaCivilNaFazenda } from "@shared/data-fazenda";
import {
  faixaDoPct,
  pctPrimeiraValida,
  PCT_DA_FAIXA,
  type FaixaQualidade,
  type VariedadeOferta,
} from "@shared/mercado-ofertas";
import { usePracas } from "@/hooks/use-cotacoes";
import {
  useContextoOferta,
  useCriarOferta,
  usePreviaChamada,
  usePublicarOferta,
} from "@/hooks/use-ofertas";

/**
 * "Ofertar carga" — cinco campos e quase nada para ler.
 *
 * ═══ O PESO DA TELA ERA O TEXTO, NÃO A QUANTIDADE DE CAMPOS ═════════════════
 *
 * Quem oferta carga é o produtor, que às vezes usa pouco celular. A versão
 * anterior era uma coluna longa de explicação com o formulário no meio — e a
 * primeira coisa que pedia era PARA QUEM ENVIAR, antes de existir o que
 * enviar. A escolha de destinatário virou a tela seguinte
 * (`/mercado/ofertas/:id`), onde ela é execução; aqui ficou só a carga.
 *
 * Saíram daqui, e é de propósito: o parágrafo sobre o preço (o essencial dele
 * está na linha do preço, e o resto no texto da mensagem, onde o COMPRADOR
 * precisa ler), o bloco "só quem você abrir os detalhes vê", o aviso de praça
 * sem compradores (pertence ao painel de envio) e o toggle "mostrar meu nome"
 * — que escolhia entre dois textos num canal onde só um deles fazia sentido.
 *
 * ⚠️ E NÃO VIRA WIZARD. Cinco campos em wizard viram cinco telas e quatro
 * "Próximo": mais toques e menos noção do que já foi respondido.
 */

/** Os três botões de "como está a banana". O número é o que vai para o campo. */
const FAIXAS: Array<{ id: FaixaQualidade; titulo: string; ajuda: string; Icone: typeof SignalHigh }> = [
  { id: "BOA", titulo: "Boa", ajuda: "80% ou mais de 1ª", Icone: SignalHigh },
  { id: "MEDIA", titulo: "Média", ajuda: "cerca de 60%", Icone: SignalMedium },
  { id: "FRACA", titulo: "Mais fraca", ajuda: "abaixo de 50%", Icone: SignalLow },
];

export default function OfertaNova() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const ativo = user?.orgTipo !== "COMPRADOR";

  const [variedade, setVariedade] = useState<VariedadeOferta>("PRATA");
  const [kg, setKg] = useState("");
  const [pct, setPct] = useState("");
  /** O input numérico nasce ESCONDIDO: os três botões bastam para a maioria. */
  const [ajustando, setAjustando] = useState(false);
  const [disponivelEm, setDisponivelEm] = useState(() => diaCivilNaFazenda(new Date()));
  const [praca, setPraca] = useState("");

  const { data: pracas = [] } = usePracas();
  const { data: contexto } = useContextoOferta(variedade, praca, ativo);
  const criar = useCriarOferta();
  const publicar = usePublicarOferta();

  // A praça abre na da org (o servidor a resolve no contexto) e só é escrita
  // no estado quando ela chega — assim o select não pisca com valor vazio.
  useEffect(() => {
    const daOrg = contexto?.alcance.praca.slug;
    if (!praca && daOrg) setPraca(daOrg);
  }, [praca, contexto?.alcance.praca.slug]);

  const volumeKg = parseInt(kg.replace(/\D/g, ""), 10) || 0;
  const ehPrata = variedade === "PRATA";
  // Nanica não tem classificação na praça: o servidor grava 100 e o texto da
  // chamada nem cita o "% de 1a". Aqui os botões simplesmente não aparecem.
  const pctNumero = ehPrata ? Number(pct.replace(",", ".")) : 100;
  const pctOk = !ehPrata || (pct.trim() !== "" && pctPrimeiraValida(pctNumero));
  /** A faixa ACESA é a que contém o número — não a que foi tocada por último. */
  const faixaAtual = ehPrata && pctOk ? faixaDoPct(pctNumero) : null;

  const previa = usePreviaChamada({
    variedade,
    volumeKg,
    pctPrimeira: pctOk ? pctNumero : 100,
    disponivelEm,
    praca,
    ativo: ativo && pctOk,
  });

  const faltando: string[] = [];
  if (volumeKg <= 0) faltando.push("o volume estimado");
  if (!pctOk) faltando.push("como está a banana");
  if (!disponivelEm) faltando.push("a data de disponibilidade");
  const podeSalvar = faltando.length === 0;

  /** Tocar a faixa SOBRESCREVE o número e fecha o ajuste fino. */
  function escolherFaixa(faixa: FaixaQualidade) {
    setPct(String(PCT_DA_FAIXA[faixa]));
    setAjustando(false);
  }

  async function salvar(publicando: boolean) {
    try {
      const criada = await criar.mutateAsync({
        variedade,
        volumeKg,
        pctPrimeira: pctNumero,
        disponivelEm,
        praca: praca || undefined,
      });
      if (!publicando) {
        toast({ title: `Rascunho ${criada.identificador} salvo`, description: "Ele fica em Ofertas até você colocar na praça." });
        setLocation("/mercado/ofertas");
        return;
      }
      await publicar.mutateAsync(criada.id);
      // Publicou: a tela da oferta é onde está o painel de envio.
      setLocation(`/mercado/ofertas/${criada.id}`);
    } catch {
      // `catch {}` vazio nunca: o produtor tem de saber que a carga NÃO foi.
      toast({ title: "Não foi possível salvar a oferta", variant: "destructive" });
    }
  }

  const cotacao = contexto?.cotacaoHoje;

  return (
    <ProdutorShell>
      <PageHeader
        titulo="Ofertar carga"
        descricao="A chamada vai anônima. Depois de publicar, você escolhe para quem mandar."
        empilharQuandoEstreito
        acoesSecundarias={
          <Button variant="outline" onClick={() => setLocation("/mercado/ofertas")} data-testid="button-cancelar-oferta">
            Cancelar
          </Button>
        }
      />

      <div className="max-w-[640px] space-y-4">
        <section className="rounded-lg border bg-card p-4 space-y-4" data-testid="form-oferta">
          <div className="space-y-1.5">
            <Label>Variedade</Label>
            <Segmented<VariedadeOferta>
              valor={variedade}
              onChange={setVariedade}
              prefixoTestId="seg-variedade"
              className="w-full [&>button]:flex-1"
              opcoes={[{ id: "PRATA", label: "Prata" }, { id: "NANICA", label: "Nanica" }]}
            />
          </div>

          {/* ─── Como está a banana ──────────────────────────────────────── */}
          {ehPrata && (
            <div className="space-y-1.5" data-testid="bloco-qualidade">
              <Label>Como está a banana</Label>
              <div className="grid grid-cols-3 gap-2">
                {FAIXAS.map(f => {
                  const acesa = faixaAtual === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => escolherFaixa(f.id)}
                      aria-pressed={acesa}
                      className={`rounded-lg border px-2 py-3 text-center transition-colors ${
                        acesa ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:bg-muted/50"
                      }`}
                      data-testid={`button-faixa-${f.id}`}
                    >
                      <f.Icone className={`h-5 w-5 mx-auto mb-1 ${acesa ? "text-primary" : "text-muted-foreground"}`} />
                      <span className="block text-sm font-semibold">{f.titulo}</span>
                      <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5">{f.ajuda}</span>
                    </button>
                  );
                })}
              </div>

              {/* O número é o contrato; a faixa é o atalho. Quem quiser o
                  número exato o digita aqui, e a faixa acende sozinha. */}
              {ajustando ? (
                <div className="flex items-center gap-2 pt-0.5">
                  <Input
                    autoFocus
                    inputMode="numeric"
                    className="h-11 w-24 font-mono text-right font-semibold"
                    placeholder="70"
                    value={pct}
                    onChange={e => setPct(e.target.value)}
                    data-testid="input-pct-primeira"
                  />
                  <span className="text-xs text-muted-foreground">% de 1ª</span>
                </div>
              ) : (
                <p className="text-[11.5px] text-muted-foreground" data-testid="texto-pct-escolhida">
                  {pctOk ? <>Vai na chamada como <b className="text-foreground">{Math.round(pctNumero)}% de 1ª</b> · </> : null}
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-foreground"
                    onClick={() => setAjustando(true)}
                    data-testid="link-ajustar-numero"
                  >
                    ajustar o número
                  </button>
                </p>
              )}
              {pct.trim() !== "" && !pctOk && (
                <p className="text-[11px] text-erro" data-testid="texto-erro-pct">Informe um número entre 0 e 100.</p>
              )}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="of-kg">Volume estimado (kg)</Label>
              <Input
                id="of-kg"
                inputMode="numeric"
                className="h-11 font-mono text-right text-lg font-semibold"
                placeholder="3000"
                value={kg}
                onChange={e => setKg(e.target.value)}
                data-testid="input-volume-kg"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="of-data">Disponível a partir de</Label>
              <Input
                id="of-data"
                type="date"
                className="h-11"
                value={disponivelEm}
                onChange={e => setDisponivelEm(e.target.value)}
                data-testid="input-disponivel-em"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Praça</Label>
            <Select value={praca} onValueChange={setPraca}>
              <SelectTrigger className="h-11 w-full" data-testid="select-praca-oferta">
                <SelectValue placeholder="Escolha a praça" />
              </SelectTrigger>
              <SelectContent>
                {pracas.map(p => (
                  <SelectItem key={p.slug} value={p.slug}>{p.nome} · {p.uf}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* ─── A linha do preço ────────────────────────────────────────── */}
          {/* Duas linhas, e nenhuma promessa: a primeira é a regra, a segunda é
              a referência de hoje, com a etiqueta do que ela é. Sem cotação a
              segunda muda de texto e NADA MAIS muda — não bloqueia publicar. */}
          <div className="rounded-lg bg-muted px-3 py-2.5 space-y-1" data-testid="bloco-preco-referencia">
            <p className="text-sm font-medium">Preço: o da praça no dia da carga.</p>
            {cotacao ? (
              <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1.5 gap-y-1">
                <span>Hoje, {fmtDiaCurto(cotacao.data)} ·</span>
                <ChipReferencial referencial={cotacao.referencial} testId="chip-referencial-oferta-nova" />
                <span className="font-mono tabular-nums text-foreground">
                  {cotacao.itens.map(i => fmtMoedaBR(i.precoKg).replace("R$ ", "")).join(" · ")}
                </span>
                {cotacao.vencida && (
                  <StatusChip kind="warn" testId="chip-cotacao-vencida">
                    vencida · há {cotacao.diasVencida} {cotacao.diasVencida === 1 ? "dia" : "dias"}
                  </StatusChip>
                )}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="texto-sem-cotacao">
                Sem cotação publicada nesta praça.
              </p>
            )}
          </div>

          {/* Fechado por padrão: quem já mandou dez chamadas não precisa reler
              o texto toda vez, e quem nunca mandou abre uma vez e confere. */}
          <details className="rounded-lg border" data-testid="details-previa">
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium select-none">
              Ver como o comprador vai ler
            </summary>
            <div className="border-t p-3">
              {/* `whitespace-pre-wrap`: as linhas em branco do texto são conteúdo —
                  sem isto o navegador colapsa tudo e a mensagem aparece grudada. */}
              <div className="rounded-2xl rounded-tl-sm border bg-background p-3 text-sm whitespace-pre-wrap font-sans">
                {previa.data?.texto ?? "…"}
              </div>
            </div>
          </details>
        </section>

        <footer className="space-y-2 pb-4">
          {/* Um botão desabilitado tem de dizer o que falta. */}
          {!podeSalvar && (
            <p className="text-xs text-atencao" data-testid="texto-falta-para-publicar">
              Falta {faltando.length === 1 ? faltando[0] : `${faltando.slice(0, -1).join(", ")} e ${faltando[faltando.length - 1]}`}.
            </p>
          )}
          <Button
            className="w-full h-12"
            disabled={!podeSalvar || criar.isPending || publicar.isPending}
            onClick={() => salvar(true)}
            data-testid="button-colocar-na-praca"
          >
            <Megaphone className="h-4 w-4 mr-1.5" />Colocar na praça
          </Button>
          <Button
            variant="outline"
            className="w-full h-11"
            disabled={!podeSalvar || criar.isPending}
            onClick={() => salvar(false)}
            data-testid="button-salvar-rascunho"
          >
            Salvar rascunho
          </Button>
        </footer>
      </div>
    </ProdutorShell>
  );
}
